import { Injectable } from '@angular/core';
//import { Workbook } from 'exceljs';
import * as XLSX from 'xlsx-js-style';
import * as fs from 'file-saver';
import { CommonDetailsService } from './common-details.service';

@Injectable({
  providedIn: 'root'
})
export class ExcelExportService {

  constructor(private commonDetailsSvc: CommonDetailsService) { }

  async generateExcel(titles: string[], data: any[], month?: number, year?: number, reportType?: string, filePrefix = 'SupervisionReport', sheetName = 'SupervisionReport') {
    // Excel Title, Header, Data
    const header = titles;
    const title = 'Supervision Report';
    const monthName = this.commonDetailsSvc.getMonthShortName(month);
    const baseFont = { name: 'Calibri', size: 11 };

  // Create workbook and worksheet
    const workbook = XLSX.utils.book_new();
    const rows = [header, ...data];
    const worksheet = XLSX.utils.aoa_to_sheet(rows);

    // Apply font styles to cells
    const range = XLSX.utils.decode_range(worksheet['!ref']!);
    for (let C = range.s.c; C <= range.e.c; C++) {
      // Header row (row 0) — bold
      const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
      if (headerCell) {
        headerCell.s = { font: { ...baseFont, bold: true } };
      }
      // Data rows — base font only
      for (let R = 1; R <= range.e.r; R++) {
        const dataCell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
        if (dataCell) {
          dataCell.s = { font: { ...baseFont } };
        }
      }
    }

    // Generate & Save Excel File
  XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    
  fs.saveAs(blob, `${filePrefix}-${reportType ?? ''}-${monthName}-${year ?? ''}.xlsx`);
  }

  /**
   * Build a workbook with one worksheet per entry. Each sheet is defined by an
   * array-of-arrays (rows). Rows listed in boldRowIndices are rendered bold.
   */
  generateMultiSheetExcel(
    sheets: { name: string; rows: any[][]; boldRowIndices?: number[] }[],
    fileName: string
  ): void {
    const baseFont = { name: 'Calibri', size: 11 };
    const workbook = XLSX.utils.book_new();
    const usedNames = new Set<string>();

    for (const sheet of sheets) {
      const worksheet = XLSX.utils.aoa_to_sheet(sheet.rows);
      const boldRows = new Set(sheet.boldRowIndices || []);

      if (worksheet['!ref']) {
        const range = XLSX.utils.decode_range(worksheet['!ref']);
        const colWidths: number[] = [];
        for (let R = range.s.r; R <= range.e.r; R++) {
          const isBold = boldRows.has(R);
          for (let C = range.s.c; C <= range.e.c; C++) {
            const cell = worksheet[XLSX.utils.encode_cell({ r: R, c: C })];
            if (cell) {
              cell.s = { font: { ...baseFont, bold: isBold } };
              const len = (cell.v ?? '').toString().length;
              colWidths[C] = Math.max(colWidths[C] || 10, len + 2);
            }
          }
        }
        worksheet['!cols'] = colWidths.map(w => ({ wch: Math.min(w, 40) }));
      }

      const safeName = this.toSafeSheetName(sheet.name, usedNames);
      usedNames.add(safeName.toLowerCase());
      XLSX.utils.book_append_sheet(workbook, worksheet, safeName);
    }

    const wbout = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([wbout], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    fs.saveAs(blob, fileName);
  }

  private toSafeSheetName(name: string, used: Set<string>): string {
    // Excel sheet names: max 31 chars and cannot contain : \ / ? * [ ]
    let safe = (name || 'Sheet').replace(/[:\\/?*[\]]/g, ' ').trim().substring(0, 31) || 'Sheet';
    let candidate = safe;
    let i = 1;
    while (used.has(candidate.toLowerCase())) {
      const suffix = `_${i++}`;
      candidate = safe.substring(0, 31 - suffix.length) + suffix;
    }
    return candidate;
  }

}

